import type { MonitoringLoopDecision } from './monitoringLoop';
import type { CoverageProofLine, PortfolioCoverageSummary } from './portfolioCoverageSummary';

export interface PortfolioValueArrivalEvidence {
  sourceLabel: string;
  completedLabel: string | null;
  freshnessLabel: string | null;
  coverageLabel: string | null;
  isExchangeLed: boolean;
}

export interface PortfolioValueArrivalAction {
  source: 'coverage' | 'monitoring';
  key: string;
  label: string;
}

function getFact(summary: PortfolioCoverageSummary, key: string) {
  return summary.facts?.find((fact) => fact.key === key)?.value ?? null;
}

function isExchangeLedSource(sourceLabel: string | null) {
  if (!sourceLabel) {
    return false;
  }

  return !/(wallet|csv|import|portfolio source)/i.test(sourceLabel);
}

function getLineSourceLabel(line: CoverageProofLine) {
  switch (line.key) {
    case 'wallets':
      return 'Connected wallets';
    case 'csv':
      return 'Latest CSV import';
    case 'exchanges':
      return 'Latest exchange sync';
    default:
      return 'Portfolio source';
  }
}

export function extractPortfolioValueArrivalEvidence(
  summary: PortfolioCoverageSummary,
): PortfolioValueArrivalEvidence | null {
  if (summary.facts?.length) {
    const sourceLabel = getFact(summary, 'latest-source') ?? 'Portfolio source';
    return {
      sourceLabel,
      completedLabel: getFact(summary, 'completed-at'),
      freshnessLabel: getFact(summary, 'freshness-delta'),
      coverageLabel: getFact(summary, 'coverage-delta'),
      isExchangeLed: isExchangeLedSource(sourceLabel),
    };
  }

  const primaryLine =
    summary.lines?.find((line) => line.key === 'exchanges' && (line.status === 'included' || line.status === 'stale')) ??
    summary.lines?.find((line) => line.key === 'wallets' && line.status === 'included') ??
    summary.lines?.find((line) => line.status === 'included' || line.status === 'stale') ??
    null;

  if (!primaryLine) {
    return null;
  }

  const includedCount = summary.lines?.filter((line) => line.status === 'included' || line.status === 'stale').length ?? 0;
  const unresolvedCount = Math.max((summary.lines?.length ?? 0) - includedCount, 0);

  return {
    sourceLabel: getLineSourceLabel(primaryLine),
    completedLabel: primaryLine.meta,
    freshnessLabel: primaryLine.meta,
    coverageLabel:
      summary.lines && summary.lines.length > 0
        ? `${includedCount} included · ${unresolvedCount} unresolved`
        : null,
    isExchangeLed: primaryLine.key === 'exchanges',
  };
}

interface PortfolioValueArrivalActionOptions {
  hasLoadedPortfolioValue?: boolean;
  preferMonitoringCheck?: boolean;
  loadedActionLabel?: string;
}

export function selectPortfolioValueArrivalAction(
  summary: PortfolioCoverageSummary,
  monitoringDecision: MonitoringLoopDecision | null,
  monitoringLabel?: string,
  options: PortfolioValueArrivalActionOptions = {},
): PortfolioValueArrivalAction | null {
  if (
    monitoringDecision?.key === 'review-drift' ||
    monitoringDecision?.key === 'open-automation' ||
    monitoringDecision?.key === 'review-active-rules'
  ) {
    return {
      source: 'monitoring',
      key: monitoringDecision.key,
      label: monitoringDecision.title,
    };
  }

  if (options.preferMonitoringCheck && monitoringDecision?.key === 'check-analytics') {
    return {
      source: 'monitoring',
      key: monitoringDecision.key,
      label: monitoringLabel ?? monitoringDecision.title,
    };
  }

  if (summary.action?.kind === 'review-portfolio') {
    return {
      source: 'coverage',
      key: summary.action.kind,
      label: options.loadedActionLabel ?? summary.action.label,
    };
  }

  if (options.hasLoadedPortfolioValue) {
    return {
      source: 'coverage',
      key: 'review-portfolio',
      label: options.loadedActionLabel ?? 'Review Portfolio',
    };
  }

  if (monitoringDecision?.key === 'check-analytics') {
    return {
      source: 'monitoring',
      key: monitoringDecision.key,
      label: monitoringLabel ?? monitoringDecision.title,
    };
  }

  if (summary.action) {
    return {
      source: 'coverage',
      key: summary.action.kind,
      label: summary.action.label,
    };
  }

  if (monitoringDecision) {
    return {
      source: 'monitoring',
      key: monitoringDecision.key,
      label: monitoringDecision.title,
    };
  }

  return null;
}
